// client/src/components/pages/CustomSummaries/CreateSummaryModal.js
import React, { useState, useEffect } from 'react';
import {
  FaTimes,
  FaCode,
  FaPlay,
  FaCheck,
  FaExclamationTriangle,
  FaChartBar,
  FaTable,
  FaChartLine,
  FaChartPie,
  FaEye,
  FaEyeSlash,
} from 'react-icons/fa';
import api from '../../../services/api';
import LoadingSpinner from '../../common/LoadingSpinner';
import ThemedSelect from '../../common/ThemedSelect';
import { toApiDateString } from '../../../utils/dateUtils';

const CreateSummaryModal = ({ summary, onClose, onSummaryCreated }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    tags: [],
    sqlQuery: '',
    queryParameters: {},
    visualizationType: 'table',
    chartConfig: null,
    isPublic: false,
  });

  const [tagInput, setTagInput] = useState('');
  const [parameterInput, setParameterInput] = useState('');
  const [parameterValue, setParameterValue] = useState('');
  const [parameterType, setParameterType] = useState('string');

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const [activeTab, setActiveTab] = useState('basic'); // basic, query, visualization, settings

  const isEditing = !!(summary && summary.SummaryID);

  // Initialize form data when editing
  useEffect(() => {
    if (summary) {
      setFormData({
        title: summary.Title || '',
        description: summary.Description || '',
        category: summary.Category || '',
        tags: summary.Tags || [],
        sqlQuery: summary.SQLQuery || '',
        queryParameters: summary.QueryParameters || {},
        visualizationType: summary.VisualizationType || 'table',
        chartConfig: summary.ChartConfig || null,
        isPublic: summary.IsPublic || false,
      });
    }
  }, [summary]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleAddParameter = () => {
    if (parameterInput.trim()) {
      const newParam = {
        type: parameterType,
        required: true,
        label: parameterInput.trim(),
        default: parameterValue || undefined,
      };

      setFormData((prev) => ({
        ...prev,
        queryParameters: {
          ...prev.queryParameters,
          [parameterInput.trim()]: newParam,
        },
      }));

      setParameterInput('');
      setParameterValue('');
      setParameterType('string');
    }
  };

  const handleRemoveParameter = (paramName) => {
    setFormData((prev) => {
      const newParams = { ...prev.queryParameters };
      delete newParams[paramName];
      return { ...prev, queryParameters: newParams };
    });
  };

  const handleValidateQuery = async () => {
    if (!formData.sqlQuery.trim()) {
      setError('SQL query is required');
      return;
    }

    try {
      setValidating(true);
      setValidationResult(null);
      setError(null);

      const response = await api.customSummaries.queryBuilder.validateQuery(
        formData.sqlQuery,
        formData.queryParameters
      );

      setValidationResult({
        isValid: true,
        message: 'Query is valid!',
      });
    } catch (err) {
      console.error('Query validation error:', err);
      setValidationResult({
        isValid: false,
        message:
          err.response?.data?.details || err.response?.data?.message || 'Query validation failed',
      });
    } finally {
      setValidating(false);
    }
  };

  const handleTestQuery = async () => {
    if (!formData.sqlQuery.trim()) {
      setError('SQL query is required');
      return;
    }

    try {
      setTesting(true);
      setTestResult(null);
      setError(null);

      // Create sample parameters for testing
      const testParams = {};
      Object.entries(formData.queryParameters).forEach(([key, config]) => {
        if (config.default !== undefined) {
          testParams[key] = config.default;
        } else {
          // Provide default test values based on type
          switch (config.type) {
            case 'date':
              testParams[key] = toApiDateString(new Date());
              break;
            case 'number':
              testParams[key] = 1;
              break;
            default:
              testParams[key] = 'test';
          }
        }
      });

      const response = await api.customSummaries.queryBuilder.testQuery(
        formData.sqlQuery,
        testParams
      );

      setTestResult({
        success: true,
        rowCount: response.data.rowCount,
        executionTime: response.data.executionTime,
        sampleData: response.data.data.slice(0, 5), // Show first 5 rows
      });
    } catch (err) {
      console.error('Query test error:', err);
      setTestResult({
        success: false,
        error: err.response?.data?.message || 'Query test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.sqlQuery.trim()) {
      setError('Title and SQL query are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (isEditing) {
        await api.customSummaries.update(summary.SummaryID, formData);
      } else {
        await api.customSummaries.create(formData);
      }

      onSummaryCreated();
    } catch (err) {
      console.error('Error saving summary:', err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.response?.data?.details ||
          `Failed to ${isEditing ? 'update' : 'create'} summary`
      );
    } finally {
      setLoading(false);
    }
  };

  const visualizationOptions = [
    { value: 'table', label: 'Table', icon: FaTable },
    { value: 'chart', label: 'Bar Chart', icon: FaChartBar },
    { value: 'line', label: 'Line Chart', icon: FaChartLine },
    { value: 'pie', label: 'Pie Chart', icon: FaChartPie },
  ];

  const categoryOptions = [
    'Operations',
    'Analytics',
    'Locations',
    'Financial',
    'Performance',
    'Custom',
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Summary' : 'Create New Summary'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <FaTimes size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'basic', label: 'Basic Info' },
              { id: 'query', label: 'SQL Query' },
              { id: 'visualization', label: 'Visualization' },
              { id: 'settings', label: 'Settings' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <form onSubmit={handleSubmit}>
            {/* Basic Info Tab */}
            {activeTab === 'basic' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter summary title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Describe what this summary does"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <ThemedSelect
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select category</option>
                    {categoryOptions.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </ThemedSelect>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-2 text-primary-600 hover:text-primary-800"
                        >
                          <FaTimes size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Add a tag"
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors duration-200"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Query Tab */}
            {activeTab === 'query' && (
              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">SQL Query *</label>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={handleValidateQuery}
                        disabled={validating}
                        className="flex items-center px-3 py-1 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded text-sm transition-colors duration-200"
                      >
                        {validating ? <LoadingSpinner size="sm" /> : <FaCheck className="mr-1" />}
                        Validate
                      </button>
                      <button
                        type="button"
                        onClick={handleTestQuery}
                        disabled={testing}
                        className="flex items-center px-3 py-1 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded text-sm transition-colors duration-200"
                      >
                        {testing ? <LoadingSpinner size="sm" /> : <FaPlay className="mr-1" />}
                        Test
                      </button>
                    </div>
                  </div>
                  <textarea
                    value={formData.sqlQuery}
                    onChange={(e) => handleInputChange('sqlQuery', e.target.value)}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                    placeholder="SELECT * FROM LexiBridgeDataView WHERE..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Only SELECT queries are allowed. Use @parameterName for dynamic parameters.
                  </p>
                </div>

                {/* Validation Result */}
                {validationResult && (
                  <div
                    className={`p-3 rounded-lg ${
                      validationResult.isValid
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-red-50 border border-red-200'
                    }`}
                  >
                    <div className="flex items-center">
                      {validationResult.isValid ? (
                        <FaCheck className="text-green-500 mr-2" />
                      ) : (
                        <FaExclamationTriangle className="text-red-500 mr-2" />
                      )}
                      <span
                        className={`text-sm ${
                          validationResult.isValid ? 'text-green-700' : 'text-red-700'
                        }`}
                      >
                        {validationResult.message}
                      </span>
                    </div>
                  </div>
                )}

                {/* Test Result */}
                {testResult && (
                  <div
                    className={`p-3 rounded-lg ${
                      testResult.success
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-red-50 border border-red-200'
                    }`}
                  >
                    {testResult.success ? (
                      <div>
                        <div className="flex items-center mb-2">
                          <FaCheck className="text-green-500 mr-2" />
                          <span className="text-sm text-green-700 font-medium">
                            Query executed successfully!
                          </span>
                        </div>
                        <div className="text-xs text-green-600">
                          Returned {testResult.rowCount} rows in {testResult.executionTime}ms
                        </div>
                        {testResult.sampleData && testResult.sampleData.length > 0 && (
                          <div className="mt-2">
                            <div className="text-xs text-green-600 mb-1">
                              Sample data (first 5 rows):
                            </div>
                            <div className="bg-surface rounded border p-2 text-xs overflow-x-auto">
                              <pre>{JSON.stringify(testResult.sampleData, null, 2)}</pre>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <FaExclamationTriangle className="text-red-500 mr-2" />
                        <span className="text-sm text-red-700">{testResult.error}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Parameters */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Query Parameters
                  </label>

                  {Object.entries(formData.queryParameters).length > 0 && (
                    <div className="mb-4 space-y-2">
                      {Object.entries(formData.queryParameters).map(([name, config]) => (
                        <div
                          key={name}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div>
                            <span className="font-medium text-sm">@{name}</span>
                            <span className="text-xs text-gray-500 ml-2">({config.type})</span>
                            {config.default && (
                              <span className="text-xs text-gray-500 ml-2">
                                Default: {config.default}
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveParameter(name)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <input
                      type="text"
                      value={parameterInput}
                      onChange={(e) => setParameterInput(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Parameter name"
                    />
                    <ThemedSelect
                      value={parameterType}
                      onChange={(e) => setParameterType(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="string">String</option>
                      <option value="number">Number</option>
                      <option value="date">Date</option>
                      <option value="boolean">Boolean</option>
                    </ThemedSelect>
                    <input
                      type="text"
                      value={parameterValue}
                      onChange={(e) => setParameterValue(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Default value"
                    />
                    <button
                      type="button"
                      onClick={handleAddParameter}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors duration-200"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Visualization Tab */}
            {activeTab === 'visualization' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Visualization Type
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {visualizationOptions.map((option) => {
                      const IconComponent = option.icon;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleInputChange('visualizationType', option.value)}
                          className={`p-4 border-2 rounded-lg text-center transition-colors duration-200 ${
                            formData.visualizationType === option.value
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          }`}
                        >
                          <IconComponent className="mx-auto mb-2" size={24} />
                          <div className="text-sm font-medium">{option.label}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {formData.visualizationType !== 'table' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Chart Configuration
                    </label>
                    <textarea
                      value={
                        formData.chartConfig ? JSON.stringify(formData.chartConfig, null, 2) : ''
                      }
                      onChange={(e) => {
                        try {
                          const config = e.target.value ? JSON.parse(e.target.value) : null;
                          handleInputChange('chartConfig', config);
                        } catch (err) {
                          // Invalid JSON, keep the text for editing
                        }
                      }}
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                      placeholder='{"xAxis": "column_name", "yAxis": "value_column", "title": "Chart Title"}'
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      JSON configuration for chart options (optional)
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Public Access</label>
                    <p className="text-sm text-gray-500">
                      Allow other users to view and execute this summary
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleInputChange('isPublic', !formData.isPublic)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                      formData.isPublic ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-surface transition-transform duration-200 ${
                        formData.isPublic ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  {formData.isPublic ? (
                    <>
                      <FaEye className="text-green-500" />
                      <span>This summary will be visible to all users</span>
                    </>
                  ) : (
                    <>
                      <FaEyeSlash className="text-gray-400" />
                      <span>This summary is private to you</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <FaExclamationTriangle className="text-red-500 mr-2" />
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-lg transition-colors duration-200"
          >
            {loading && <LoadingSpinner size="sm" className="mr-2" />}
            {isEditing ? 'Update Summary' : 'Create Summary'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSummaryModal;
